import { useState, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { DashboardCard } from './DashboardCard';
import { FilterPanel } from './FilterPanel';
import { SiteDetailModal } from './SiteDetailModal';
import { GeographicDataModal } from './GeographicDataModal';
import { VersionDataModal } from './VersionDataModal';
import { VersionDeploymentByRegionChart } from './charts/VersionDeploymentByRegionChart';
import { SitesAnalysisModal } from './SitesAnalysisModal';
import { SimulationStatisticsModal } from './SimulationStatisticsModal';
import { HexagonPattern, HexagonStats } from './HexagonPattern';
import { AnimatedCounter } from './AnimatedCounter';
import { CustomerTypePieChart } from './charts/CustomerTypePieChart';
import { RegionalHorizontalBarChart } from './charts/RegionalHorizontalBarChart';
import WorldWireframeMap from './WorldWireframeMap';
import { useData } from '../context/DataContext';
import { SiteData, FilterOptions } from '../types';
import { 
  Activity, 
  Users, 
  Globe, 
  Building, 
  Settings,
  Eye,
  TrendingUp,
  MapPin,
  CheckCircle,
  PieChart as PieChartIcon,
  BarChart3,
  ChevronUp,
  ChevronDown,
  AlertTriangle
} from 'lucide-react';
import heroImage from '../assets/mentor-learn-hero.jpg';
import dataBackgroundVideo from '../assets/data-background.mp4';
import { CountryDetailsModal } from './map/CountryDetailsModal';
import { AIAgent } from './AIAgent';
import UserMenu from './UserMenu';
import { formatTableDate } from '../utils/dateUtils';

interface DashboardProps {
  onAdminAccess: () => void;
}

export const Dashboard = ({ onAdminAccess }: DashboardProps) => {
  const { siteData, simulationStats, isDataPublished, isLoading, error } = useData();
  const [filters, setFilters] = useState<FilterOptions>({
    region: 'all',
    siteName: 'all',
    customer: 'all'
  });
  const [selectedSite, setSelectedSite] = useState<SiteData | null>(null);
  const [showSiteDetail, setShowSiteDetail] = useState(false);
  const [showGeographicModal, setShowGeographicModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showSitesAnalysisModal, setShowSitesAnalysisModal] = useState(false);
  const [showSimulationStatsModal, setShowSimulationStatsModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedCustomerType, setSelectedCustomerType] = useState<string>('');
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false);
  const [isCustomerTypeModalOpen, setIsCustomerTypeModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof SiteData | 'status';
    direction: 'asc' | 'desc';
  } | null>(null);

  // Use only the real data from Google Sheets - no fallback to mock data
  const currentData = siteData.length > 0 ? siteData : [];

  // Filter and sort data based on current settings
  const filteredAndSortedData = useMemo(() => {
    let filtered = currentData.filter(site => {
      // Use country/region filtering instead of wRegion
      let regionMatch = filters.region === 'all';
      
      if (!regionMatch) {
        if (filters.region === 'North America') {
          regionMatch = site.country === 'United States';
        } else if (site.country === 'United States') {
          // For US sites, check against region column (US subregions)
          regionMatch = site.region === filters.region;
        } else {
          // For non-US sites, check against region column
          regionMatch = site.region === filters.region;
        }
      }
        
      return regionMatch &&
             (filters.siteName === 'all' || site.siteName === filters.siteName) &&
             (filters.customer === 'all' || site.customer === filters.customer);
    });

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        let aValue: any, bValue: any;
        
        if (sortConfig.key === 'status') {
          // Status priority: Active (3) > Connected (2) > Offline (1)
          aValue = a.activeUsers >= 3 ? 3 : (a.isOnline ? 2 : 1);
          bValue = b.activeUsers >= 3 ? 3 : (b.isOnline ? 2 : 1);
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }

        // Handle different data types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [currentData, filters, sortConfig]);

  // Use the filtered and sorted data for calculations
  const filteredData = filteredAndSortedData;

  // Calculate dashboard statistics
  const stats = useMemo(() => {
    const totalSessionsLastMonth = filteredData.reduce((sum, site) => sum + site.numSessionsLastMonth, 0);
    const totalSessionsLast12Months = filteredData.reduce((sum, site) => sum + site.numSessionsOnServerLast12Month, 0);
    
    // Active Sites: sites with at least 3 active users
    const activeSites = filteredData.filter(site => 
      site.activeUsers >= 3
    ).length;
    
    // Connected Sites: online sites with fewer than 3 active users
    const connectedSites = filteredData.filter(site => 
      site.isOnline && site.activeUsers < 3
    ).length;
    
    // Offline Sites: offline sites
    const offlineSites = filteredData.filter(site => 
      !site.isOnline
    ).length;
    
    // Unique Customers: customers from active sites (>=3 active users)
    const activeSitesData = filteredData.filter(site => 
      site.activeUsers >= 3
    );
    const uniqueCustomers = new Set(activeSitesData.map(site => site.customer)).size;
    
    // Unique Customers from inactive sites
    const inactiveSitesData = filteredData.filter(site => 
      site.activeUsers < 3
    );
    const uniqueInactiveCustomers = new Set(inactiveSitesData.map(site => site.customer)).size;
    
    // Calculate actual active users from sites with recent activity
    const activeUsersLastMonth = filteredData
      .filter(site => site.activeUsers > 0)
      .reduce((sum, site) => sum + site.activeUsers, 0);
    
    // Calculate total session time in minutes from the actual data
    const sessionTimeMinutes = filteredData.reduce((sum, site) => sum + site.totalSimulationTimeMinutes, 0);

    // Regional statistics - Group by main regions with proper structure for chart
    const regionStats = filteredData.reduce((acc, site) => {
      // Determine main region and sub-level (subregion for US, country for others)
      let mainRegion, subLevel;
      
      if (site.country === 'United States') {
        mainRegion = 'North America';
        subLevel = site.region || 'Unknown'; // US subregion
      } else {
        // Map ALL non-US sites to exactly 4 main regions
        const region = site.region?.toLowerCase() || '';
        
        if (region.includes('south america') || region === 'south america') {
          mainRegion = 'South America';
          subLevel = site.country;
        } else if (region.includes('europe') || region === 'europe') {
          mainRegion = 'Europe';
          subLevel = site.country;
        } else if (region.includes('apac') || region === 'apac' || region.includes('asia') || region.includes('pacific')) {
          mainRegion = 'APAC';
          subLevel = site.country;
        } else {
          // All other regions (including NA variations, Middle East, etc.) go to North America
          mainRegion = 'North America';
          subLevel = site.country;
        }
      }
      
      const key = `${mainRegion}-${subLevel}`;
      
      if (!acc[key]) {
        acc[key] = { 
          region: mainRegion, 
          country: subLevel, 
          sessions: 0, 
          sites: 0
        };
      }
      acc[key].sessions += site.numSessionsOnServerLast12Month;
      acc[key].sites += 1;
      return acc;
    }, {} as Record<string, any>);

    // Customer type statistics
    const customerTypeStats = filteredData.reduce((acc, site) => {
      if (!acc[site.siteType]) {
        acc[site.siteType] = { type: site.siteType, sessions: 0, sites: 0 };
      }
      acc[site.siteType].sessions += site.numSessionsOnServerLast12Month;
      acc[site.siteType].sites += 1;
      return acc;
    }, {} as Record<string, any>);

    // Version statistics - updated definitions
    const versionStats = filteredData.reduce((acc, site) => {
      if (!acc[site.mlVersion]) {
        acc[site.mlVersion] = { version: site.mlVersion, activeSites: 0, connectedSites: 0, offlineSites: 0 };
      }
      if (site.activeUsers >= 3) {
        acc[site.mlVersion].activeSites += 1;
      } else if (site.isOnline) {
        acc[site.mlVersion].connectedSites += 1;
      } else {
        acc[site.mlVersion].offlineSites += 1;
      }
      return acc;
    }, {} as Record<string, any>);

    // Version deployment by region (for latest version - highest version number)
    // Only include truly active sites (>=3 active users)
    const trulyActiveSites = filteredData.filter(site => site.activeUsers >= 3);
    
    const sortedVersions = Object.values(versionStats).sort((a: any, b: any) => {
      // Parse version numbers for proper comparison (e.g., "2.1.0" vs "10.0.0")
      const parseVersion = (version: string) => {
        const parts = version.split('.').map(part => parseInt(part.replace(/[^\d]/g, '')) || 0);
        return parts[0] * 10000 + (parts[1] || 0) * 100 + (parts[2] || 0);
      };
      return parseVersion(b.version) - parseVersion(a.version);
    });
    const latestVersion = sortedVersions[0]?.version;
    
    const versionDeploymentByRegion = latestVersion ? Object.values(regionStats).map((regionStat: any) => {
      // For version deployment, use country/region-based filtering
      const regionActiveSites = trulyActiveSites.filter(site => {
        let siteDisplayRegion;
        if (site.country === 'United States') {
          siteDisplayRegion = 'North America';
        } else {
          siteDisplayRegion = site.region;
        }
        return siteDisplayRegion === regionStat.region;
      });
      const latestVersionSites = regionActiveSites.filter(site => site.mlVersion === latestVersion);
      const totalSites = regionActiveSites.length;
      const latestVersionPercentage = totalSites > 0 ? (latestVersionSites.length / totalSites) * 100 : 0;
      
      return {
        region: regionStat.region,
        latestVersionPercentage,
        totalSites,
        latestVersionSites: latestVersionSites.length
      };
    }).filter(item => item.totalSites > 0) : [];

    return {
      totalSessionsLastMonth,
      totalSessionsLast12Months,
      totalSites: filteredData.length,
      activeSites,
      connectedSites,
      offlineSites,
      uniqueCustomers,
      uniqueInactiveCustomers,
      activeUsersLastMonth,
      sessionTimeMinutes,
      regionStats: Object.values(regionStats),
      customerTypeStats: Object.values(customerTypeStats),
      versionStats: Object.values(versionStats).sort((a: any, b: any) => {
        // Parse version numbers for proper comparison (e.g., "2.1.0" vs "10.0.0")
        const parseVersion = (version: string) => {
          const parts = version.split('.').map(part => parseInt(part.replace(/[^\d]/g, '')) || 0);
          return parts[0] * 10000 + (parts[1] || 0) * 100 + (parts[2] || 0);
        };
        return parseVersion(b.version) - parseVersion(a.version);
      }),
      versionDeploymentByRegion,
      latestVersion
    };
  }, [filteredData]);

  // Get unique values for filter dropdowns - use country/region columns
  const availableRegions = useMemo(() => {
    const regionSet = new Set<string>();
    
    currentData.forEach(site => {
      if (site.country === 'United States') {
        regionSet.add('North America');
        // Add US subregions (from region column)
        if (site.region) {
          regionSet.add(site.region);
        }
      } else {
        // For non-US sites, use region as-is
        if (site.region) {
          regionSet.add(site.region);
        }
      }
    });
    
    return Array.from(regionSet).sort().map(region => ({
      value: region,
      label: region === 'North America' ? region : (currentData.some(site => site.country === 'United States' && site.region === region) ? `${region} (US)` : region)
    }));
  }, [currentData]);
  const availableSites = useMemo(() => 
    [...new Set(currentData.map(site => site.siteName).filter(siteName => siteName && siteName.trim() !== ''))], 
    [currentData]
  );
  const availableCustomers = useMemo(() => 
    [...new Set(currentData.map(site => site.customer).filter(customer => customer && customer.trim() !== ''))], 
    [currentData]
  );

  const handleSiteDetail = (site: SiteData) => {
    setSelectedSite(site);
    setShowSiteDetail(true);
  };

  const handleSort = (key: keyof SiteData | 'status') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof SiteData | 'status') => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ChevronUp className="w-4 h-4 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4" />
      : <ChevronDown className="w-4 h-4" />;
  };

  const handleCountryClick = (countryName: string) => {
    setSelectedCountry(countryName);
    setIsCountryModalOpen(true);
  };

  const handleRegionClick = (regionName: string) => {
    setSelectedRegion(regionName);
    setIsRegionModalOpen(true);
  };

  const handleCloseCountryModal = () => {
    setIsCountryModalOpen(false);
    setSelectedCountry('');
  };

  const handleCloseRegionModal = () => {
    setIsRegionModalOpen(false);
    setSelectedRegion('');
  };

  const handleCustomerTypeClick = (customerType: string) => {
    setSelectedCustomerType(customerType);
    setIsCustomerTypeModalOpen(true);
  };

  const handleCloseCustomerTypeModal = () => {
    setIsCustomerTypeModalOpen(false);
    setSelectedCustomerType('');
  };

  // Filter sites by selected country, region, or customer type
  const countrySites = useMemo(() => {
    if (!selectedCountry) return [];
    return currentData.filter(site => {
      // For US subregions, match against region column
      if (site.country === 'United States' && site.region === selectedCountry) {
        return true;
      }
      return site.country === selectedCountry;
    });
  }, [currentData, selectedCountry]);

  const regionSites = useMemo(() => {
    if (!selectedRegion) return [];
    return currentData.filter(site => {
      if (selectedRegion === 'North America') {
        return site.country === 'United States';
      }
      // For US subregions, match against region column
      if (site.country === 'United States' && site.region === selectedRegion) {
        return true;
      }
      return site.region === selectedRegion;
    });
  }, [currentData, selectedRegion]);

  const customerTypeSites = currentData.filter(site => site.siteType === selectedCustomerType);

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Show loading state */}
      {isLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">Loading MentorLearn Cloud Data</h2>
            <p className="text-muted-foreground">Fetching latest data...</p>
          </div>
        </div>
      )}

      {/* Show error state */}
      {error && !isLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <div className="text-destructive text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">Unable to Load Data</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground">Please check your internet connection and try refreshing the page.</p>
          </div>
        </div>
      )}

      {/* Show empty state if no data */}
      {!isLoading && !error && currentData.length === 0 && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <div className="text-muted-foreground text-6xl mb-4">📊</div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">No Data Available</h2>
            <p className="text-muted-foreground">The data source appears to be empty or inaccessible.</p>
          </div>
        </div>
      )}

      {/* Main dashboard content - only show when we have data */}
      {!isLoading && !error && currentData.length > 0 && (
        <>
          {/* Hero Section */}
          <div className="relative bg-gradient-to-r from-red-500/20 via-red-400/15 to-pink-500/20">
            <img 
              src={heroImage} 
              alt="MentorLearn Cloud Portal" 
              className="w-full h-32 object-cover opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/30 via-transparent to-pink-500/30" />
            
            {/* Data elements overlay */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-2 left-4 text-red-300 text-xs font-mono">01001101</div>
              <div className="absolute top-8 right-8 text-pink-300 text-xs font-mono">{"{ data: 'cloud' }"}</div>
              <div className="absolute bottom-4 left-12 text-red-400 text-xs font-mono">SELECT * FROM sites</div>
              <div className="absolute bottom-8 right-4 text-pink-400 text-xs font-mono">Analytics.run()</div>
              <div className="absolute top-4 left-1/3 text-red-200 text-xs font-mono">∑ Σ ∫</div>
              <div className="absolute bottom-2 left-1/2 text-pink-200 text-xs font-mono">ML.predict()</div>
            </div>
            
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <img 
                    src="/lovable-uploads/ab8933d4-2a6f-4a0f-a02a-41e628c09e83.png" 
                    alt="Surgical Science Logo" 
                    className="h-8 object-contain"
                  />
                </div>
                <h1 className="text-xl font-bold text-foreground mb-1">MentorLearn Cloud Portal</h1>
                <p className="text-sm text-muted-foreground">Global Site Statistics & Analytics</p>
              </div>
            </div>
            <div className="absolute top-4 right-4 flex items-center gap-2">
              <UserMenu />
              <Button 
                onClick={onAdminAccess}
                variant="outline"
                size="sm"
                className="bg-white/10 border-border text-foreground hover:bg-muted"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin Access
              </Button>
            </div>
          </div>

          <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* MentorLearn Cloud Statistics Section */}
            <Card className="relative p-8 shadow-card overflow-hidden border-red-100">
              {/* Video Background */}
              <div className="absolute inset-0 w-full h-full">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full h-full object-cover"
                >
                  <source src="https://surgicalscience.com/wp-content/uploads/2023/11/Homepage_banner_rob-heart-lap.mp4" type="video/mp4" />
                </video>
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/50" />
              
              {/* Content */}
              <div className="relative z-20">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">MentorLearn Cloud Statistics</h2>
                  <p className="text-white text-lg drop-shadow-md">All data in one place</p>
                </div>
                <div className="grid grid-cols-2 gap-8 justify-items-center">
                  <AnimatedCounter 
                    value={stats.activeSites} 
                    label="Active Sites"
                    duration={1500}
                    inactiveValue={stats.connectedSites + stats.offlineSites}
                  />
                  <AnimatedCounter 
                    value={stats.activeUsersLastMonth} 
                    label="Active Users"
                    duration={1500}
                  />
                  <AnimatedCounter 
                    value={stats.totalSessionsLastMonth} 
                    label="Number of Session Last Month"
                    duration={1500}
                  />
                  <AnimatedCounter 
                    value={stats.uniqueCustomers} 
                    label="Unique Customers"
                    duration={1500}
                    inactiveValue={stats.uniqueInactiveCustomers}
                  />
                </div>
              </div>
            </Card>

            {/* Filter Panel */}
            <FilterPanel
              filters={filters}
              onFilterChange={setFilters}
              availableRegions={availableRegions}
              availableSites={availableSites}
              availableCustomers={availableCustomers}
            />

            {/* Geographic and Version Data Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card 
                className="p-6 shadow-card hover:shadow-lg transition-shadow duration-200"
                onClick={(e) => {
                  // Only open modal if clicking outside the map area
                  if (e.target === e.currentTarget || !e.currentTarget.querySelector('.map-container')?.contains(e.target as Node)) {
                    setShowGeographicModal(true);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <div className="text-center mb-4">
                  <h2 className="text-xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Geographic Data
                  </h2>
                  <p className="text-muted-foreground text-sm">Click to view detailed world map</p>
                </div>
                <div className="h-48 map-container">
                  <WorldWireframeMap 
                    height={192}
                    stroke="hsl(var(--primary))"
                    hoverStroke="hsl(var(--secondary))"
                    showGraticule={false}
                    showPoints={true}
                    pointCount={60}
                    siteData={filteredData}
                    onMapClick={() => setShowGeographicModal(true)}
                  />
                </div>
              </Card>

              <Card 
                className="p-6 shadow-card cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={() => setShowVersionModal(true)}
              >
                <div className="text-center mb-4">
                  <h2 className="text-xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                    <Activity className="w-5 h-5 text-secondary" />
                    MentorLearn Cloud Versions Data
                  </h2>
                  <p className="text-muted-foreground text-sm">Click to view version distribution</p>
                </div>
                <div className="h-48">
                  {stats.versionDeploymentByRegion && stats.versionDeploymentByRegion.length > 0 ? (
                    <div className="h-full">
                      <div className="text-center mb-2">
                        <div className="text-sm font-medium text-muted-foreground">
                          Latest Version ({stats.latestVersion}) Deployment by Region
                        </div>
                        <div className="text-xs text-muted-foreground">
                          (Active sites with ≥10 sessions last 12 months)
                        </div>
                      </div>
                      <div className="h-40">
                        <VersionDeploymentByRegionChart data={stats.versionDeploymentByRegion} />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-primary mb-2">
                          {stats.versionStats.length}
                        </div>
                        <div className="text-sm text-muted-foreground">Different Versions</div>
                        <div className="mt-4 text-xs text-muted-foreground">
                          Most common: {stats.versionStats[0]?.version || 'N/A'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Regional and Customer Type Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6 shadow-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Sessions by Region & Country
                </h2>
                <RegionalHorizontalBarChart 
                  data={stats.regionStats} 
                  onCountryClick={handleCountryClick}
                  onRegionClick={handleRegionClick}
                />
              </Card>

              <Card className="p-6 shadow-card">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5 text-secondary" />
                  Sites by Customer Type
                </h2>
                <CustomerTypePieChart 
                  data={stats.customerTypeStats} 
                  onCustomerTypeClick={handleCustomerTypeClick}
                />
              </Card>
            </div>

            {/* Simulation Statistics Card */}
            <Card 
              className="p-6 shadow-card cursor-pointer hover:shadow-lg transition-shadow duration-200 relative group"
              onClick={() => setShowSimulationStatsModal(true)}
            >
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-accent" />
                Simulation Statistics
                <Badge variant="outline" className="ml-2 text-xs">
                  Last 12 Months
                </Badge>
              </h2>
              <p className="text-muted-foreground text-sm mb-4">
                Click to view detailed treemap analysis of simulation usage data. Hover to preview.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{simulationStats.length}</div>
                  <div className="text-sm text-muted-foreground">Total Records</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary">
                    {new Set(simulationStats.map(s => s.simulatorType)).size}
                  </div>
                  <div className="text-sm text-muted-foreground">Simulator Types</div>
                </div>
              </div>
              
              {/* Hover Preview */}
              <div className="absolute top-0 left-full ml-4 w-80 bg-background border border-border rounded-lg shadow-lg p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                <h3 className="text-sm font-semibold mb-2">Simulator Usage Preview</h3>
                <div className="space-y-1">
                  {Object.entries(
                    simulationStats.reduce((acc, stat) => {
                      acc[stat.simulatorType] = (acc[stat.simulatorType] || 0) + stat.sessionsLast12Months;
                      return acc;
                    }, {} as Record<string, number>)
                  )
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 5)
                    .map(([type, sessions]) => (
                      <div key={type} className="flex justify-between text-xs">
                        <span className="truncate">{type}</span>
                        <span className="font-mono">{sessions.toLocaleString()}</span>
                      </div>
                    ))}
                </div>
                <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                  Click card to see full treemap visualization
                </div>
              </div>
            </Card>

            {/* AI Agent */}
            <AIAgent />

            {/* Sites Table */}
            <Card className="shadow-card">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold flex items-center gap-2">
                      <Users className="w-5 h-5 text-success" />
                      Site Status Overview
                    </h2>
                    <p className="text-muted-foreground mt-1">Click on any site to view detailed information</p>
                  </div>
                  <Button
                    onClick={() => setShowSitesAnalysisModal(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    Sites Analysis
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('siteName')}
                      >
                        <div className="flex items-center gap-1">
                          Site Name
                          {getSortIcon('siteName')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-1">
                          Status
                          {getSortIcon('status')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('customer')}
                      >
                        <div className="flex items-center gap-1">
                          Customer
                          {getSortIcon('customer')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('region')}
                      >
                        <div className="flex items-center gap-1">
                          Region
                          {getSortIcon('region')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('numSessionsLastMonth')}
                      >
                        <div className="flex items-center gap-1">
                          Sessions (Last Month)
                          {getSortIcon('numSessionsLastMonth')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('numSessionsOnServerLast12Month')}
                      >
                        <div className="flex items-center gap-1">
                          Sessions (12 Months)
                          {getSortIcon('numSessionsOnServerLast12Month')}
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort('lastUsage')}
                      >
                        <div className="flex items-center gap-1">
                          Last Usage
                          {getSortIcon('lastUsage')}
                        </div>
                      </TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredData.map((site) => (
                      <TableRow key={site.siteName} className="hover:bg-muted/50">
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://${site.siteName}.mentorlearn.com`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:text-primary transition-colors group relative"
                              title={`Visit ${site.siteName}.mentorlearn.com`}
                            >
                              {site.siteName}
                              <span className="absolute -top-8 left-0 bg-popover text-popover-foreground px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 border shadow-md">
                                🔗 {site.siteName}.mentorlearn.com
                              </span>
                            </a>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            site.activeUsers >= 3 
                              ? "default" 
                              : site.isOnline 
                                ? "secondary" 
                                : "outline"
                          }
                          className={
                            site.activeUsers >= 3 
                              ? "" 
                              : site.isOnline 
                                ? "" 
                                : "border-red-300 text-red-600 bg-red-50"
                          }>
                            {site.activeUsers >= 3 
                              ? 'Active' 
                              : site.isOnline 
                                ? 'Connected' 
                                : 'Offline'}
                          </Badge>
                        </TableCell>
                        <TableCell>{site.customer}</TableCell>
                        <TableCell>{site.region}</TableCell>
                        <TableCell>{site.numSessionsLastMonth}</TableCell>
                        <TableCell>{site.numSessionsOnServerLast12Month}</TableCell>
                        <TableCell>{formatTableDate(site.lastUsage, 'No data')}</TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleSiteDetail(site)}
                            variant="outline"
                            size="sm"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>

          {/* Modals */}
          <SiteDetailModal
            site={selectedSite}
            isOpen={showSiteDetail}
            onClose={() => setShowSiteDetail(false)}
            simulationStats={simulationStats}
          />
          <GeographicDataModal
            isOpen={showGeographicModal}
            onClose={() => setShowGeographicModal(false)}
            siteData={filteredData}
          />
          <VersionDataModal
            isOpen={showVersionModal}
            onClose={() => setShowVersionModal(false)}
            data={stats.versionStats}
            siteData={filteredData}
            latestVersion={stats.latestVersion}
          />

          {/* Country Details Modal */}
          <CountryDetailsModal
            isOpen={isCountryModalOpen}
            onClose={handleCloseCountryModal}
            countryName={selectedCountry}
            sites={countrySites}
            onSiteClick={handleSiteDetail}
          />

          {/* Region Details Modal */}
          <CountryDetailsModal
            isOpen={isRegionModalOpen}
            onClose={handleCloseRegionModal}
            countryName={selectedRegion}
            sites={regionSites}
            onSiteClick={handleSiteDetail}
          />

          {/* Customer Type Details Modal */}
          <CountryDetailsModal
            isOpen={isCustomerTypeModalOpen}
            onClose={handleCloseCustomerTypeModal}
            countryName={`${selectedCustomerType} Sites`}
            sites={customerTypeSites}
            onSiteClick={handleSiteDetail}
          />

          {/* Simulation Statistics Modal */}
          <SimulationStatisticsModal
            isOpen={showSimulationStatsModal}
            onClose={() => setShowSimulationStatsModal(false)}
            simulationStats={simulationStats}
            siteData={currentData}
          />

          {/* Sites Analysis Modal */}
          <SitesAnalysisModal
            isOpen={showSitesAnalysisModal}
            onClose={() => setShowSitesAnalysisModal(false)}
            siteData={filteredData}
            onSiteClick={handleSiteDetail}
          />
        </>
      )}
    </div>
  );
};